import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Form, Input, Select, Space, Typography, message } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';
import { useProjects } from '@/hooks/useProjects';
import { buildProjectIdToLabelMap, resolveProjectDisplayFromMap } from '@/utils/pmvDisplay';

const { Title } = Typography;

const PmvSiteReceiptFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { data: projects = [] } = useProjects();
  const projectLabels = buildProjectIdToLabelMap(projects);

  const allocQ = useQuery({
    queryKey: ['pmv-allocation', id],
    queryFn: () => pmvService.getAllocation(id!),
    enabled: Boolean(id),
  });

  const alloc = allocQ.data;
  const dispatchId = (alloc as { dispatch_id?: string } | undefined)?.dispatch_id;

  const receiveMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => pmvService.receiveDispatch(dispatchId!, body),
    onSuccess: () => {
      message.success('Site receipt recorded');
      navigate(`/asset/pmv/allocations/${id}`);
    },
    onError: (e: Error) => message.error(e.message),
  });

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Site receipt"
          documentNumber={alloc?.allocation_number ?? ''}
        />
      }
    >
      <Title level={5}>
        Receive on site
        {alloc?.project_id
          ? ` — ${resolveProjectDisplayFromMap(projectLabels, alloc.project_id)}`
          : ''}
      </Title>
      {!dispatchId && (
        <Typography.Text type="danger">No dispatch found for this allocation.</Typography.Text>
      )}
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640, marginTop: 16 }}
        initialValues={{ receipt_condition: 'ACCEPTED' }}
        onFinish={(v) => receiveMut.mutate(v)}
      >
        <Form.Item name="receipt_condition" label="Condition" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'ACCEPTED', label: 'Accepted' },
              { value: 'ACCEPTED_WITH_OBSERVATION', label: 'Accepted with observation' },
              { value: 'REJECTED', label: 'Rejected' },
            ]}
          />
        </Form.Item>
        <Form.Item name="receipt_notes" label="Notes">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={receiveMut.isPending} disabled={!dispatchId}>
            Record receipt
          </Button>
          <Button onClick={() => navigate(`/asset/pmv/allocations/${id}`)}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};

export default PmvSiteReceiptFormPage;
